/**
 * Add, replace or re-verify the reseller's card on file (TMMRSL-0046, DLG-001). Card capture is
 * the reseller-scope `CardCapture` (single-flight, the duplicate guard). `PUT /billing/card` is
 * retried after `409 CONCURRENT_UPDATE` (TMMAPI-0102); a `requires_action` answer runs 3-D Secure
 * and then sends the SAME body once more, as tmm-api requires — the server derives the
 * idempotency key, so the replay is safe. Errors are translated, never the server's text.
 */
import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError, backendCode } from '@/api/errors';
import { putCardWithRetry } from '@/api/resellerBilling';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { describeApiError } from '@/components/useApiErrorHandler';
import { useToast } from '@/components/useToast';
import { CardCapture } from '@/features/billing/CardCapture';
import { completeResellerClientAction } from '@/features/billing/completeClientAction';

export type AddCardMode = 'add' | 'replace' | 'reverify';

export interface AddCardDialogProps {
  open: boolean;
  mode: AddCardMode;
  /** A charge is waiting (FAILED / REQUIRES_ACTION): saving re-sends it within the hour. */
  hasOpenCharges: boolean;
  onClose: () => void;
  onDone: () => void;
}

export function AddCardDialog({ open, mode, hasOpenCharges, onClose, onDone }: AddCardDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  // A failed save. Only an API failure carries a Reference (TMMRSL-0072); the 3-D Secure outcome
  // is the card's own message.
  const [error, setError] = useState<{ message: string; requestId?: string } | null>(null);

  function handleClose() {
    if (submitting) return;
    setError(null);
    onClose();
  }

  function errorText(err: unknown): string {
    switch (backendCode(err)) {
      case 'VAULT_FAILED':
        return t('resellerBilling.error.declined');
      case 'MERCHANT_OF_RECORD_UNSET':
      case 'PAY_API_UNAVAILABLE':
        return t('resellerBilling.error.unavailable');
      case 'CONCURRENT_UPDATE':
        return t('resellerBilling.error.busy');
      default:
        return describeApiError(err, t).message;
    }
  }

  async function handleToken(token: string): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      let result = await putCardWithRetry(token);
      if (result.kind === 'requiresAction') {
        const next = await completeResellerClientAction(result.clientAction);
        if (next.kind !== 'completed') {
          setError({ message: t('resellerBilling.error.verifyFailed') });
          return;
        }
        result = await putCardWithRetry(token);
        if (result.kind === 'requiresAction') {
          setError({ message: t('resellerBilling.error.verifyFailed') });
          return;
        }
      }
      toast(
        hasOpenCharges
          ? t('resellerBilling.addDialog.savedResend')
          : t('resellerBilling.addDialog.saved'),
      );
      onDone();
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError && err.requestId
          ? { message: errorText(err), requestId: err.requestId }
          : { message: errorText(err) },
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t(`resellerBilling.addDialog.title.${mode}`)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2">{t('resellerBilling.addDialog.intro')}</Typography>
          {mode === 'replace' ? (
            <Typography variant="body2">{t('resellerBilling.addDialog.replaceNote')}</Typography>
          ) : null}
          {hasOpenCharges ? (
            <Typography variant="body2">{t('resellerBilling.addDialog.resendNote')}</Typography>
          ) : null}
          <ActionErrorAlert error={error} />
          <CardCapture
            scope="reseller"
            submitLabel={t('resellerBilling.addDialog.save')}
            onToken={handleToken}
            busy={submitting}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
